import React, { useEffect } from 'react';
import { useStore } from '../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../services/general/error';
import ValidationService from '../../../../services/validation';
import GeneralService from '../../../../services/general';

// props

// components
import { DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../typography/label';
import SelectSubsidiary from '../../../uiframeworks/forms/selectSubsidiary';
import { IVesselResourceProps } from '../../../../props/vessels/vessel';
import VesselService from '../../../../services/vessels/vessel';
import SelectCountry from '../../../uiframeworks/forms/data/geographics/country';
import SelectShipType from '../../../uiframeworks/forms/data/fleets/shipType';

interface VesselFormProps {
    vesselId?: string;
    onDismissed(refresh?: boolean, status?: string, vessel?: IVesselResourceProps): void;
}

const VesselForm: React.FC<VesselFormProps> = (props: VesselFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IVesselResourceProps>>({
        id: GeneralService.guid()
    });
    const [error, setError] = React.useState<any>({});
    const mode = props.vesselId ? 'update' : 'create';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _data = data;

            if (props.vesselId) {
                const vessel = await VesselService.read(props.vesselId);
                _data = vessel;
            }

            setData({ ..._data });
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_vessel_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get vessel. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (!data.subsidiary || !data.flag || !data.type) {
            return true;
        } else if ((data.name || "").trim() === "" || (data.class || "").trim() === "" || (data.construction || "").trim() === "" || (data.callSign || "").trim() === "") {
            return true;
        } else if ((data.loa || "").trim() === "" || (data.lbp || "").trim() === "" || (data.breadth || "").trim() === "" || (data.depth || "").trim() === "" || (data.maximumLoad || "").trim() === "") {
            return true;
        } else if ((data.grossTonage || "").trim() === "" || (data.nettTonage || "").trim() === "") {
            return true;
        } else if (Object.values(error).filter(Boolean).length > 0) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, registrationNumber, notes, subsidiary, type, flag, construction, callSign, loa, lbp, breadth, depth, maximumLoad, nettTonage, grossTonage, yearMade } = data;

            // create form data
            const fd = new FormData();
            fd.append('name', name || "");
            fd.append('registrationNumber', registrationNumber || "");
            fd.append('notes', notes || "");
            fd.append('construction', construction || "");
            fd.append('callSign', callSign || "");
            fd.append('loa', loa || "");
            fd.append('lbp', lbp || "");
            fd.append('breadth', breadth || "");
            fd.append('depth', depth || "");
            fd.append('maximumLoad', maximumLoad || "");
            fd.append('nettTonage', nettTonage || "");
            fd.append('grossTonage', grossTonage || "");
            fd.append('yearMade', yearMade || "");
            fd.append('subsidiaryId', subsidiary?.id || "");
            fd.append('typeId', type?.id || "");
            fd.append('flagId', flag?.key || "");
            fd.append('class', data.class || "");

            let vessel = undefined;
            if (props.vesselId) {
                vessel = await VesselService.update(props.vesselId, fd);
            } else {
                vessel = await VesselService.create(fd);
            }

            banner.add({
                key: 'request_vessel_success',
                variant: 'success',
                icon: faCheck,
                text: `Vessel "${name}" has been ${mode}d.`
            });
            props.onDismissed(true, undefined, vessel);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={mode === 'create' ? 'Create Vessel' : 'Update Vessel'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={mode === 'create' ? 'Create' : 'Update'} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating vessel ..." : "Updating vessel ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <TextField label={"Name"}
                        value={data.name}
                        required
                        disabled={submitting}
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.name = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                            error.name = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.name} />
                    <TextField label={"Registration number"}
                        value={data.registrationNumber}
                        required
                        disabled={submitting}
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.registrationNumber = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                            error.registrationNumber = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.registrationNumber} />
                    <TextField label={"Notes"}
                        value={data.notes}
                        disabled={submitting}
                        multiline
                        rows={5}
                        autoAdjustHeight
                        resizable={false}
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.notes = value;

                            const validation = ValidationService.combination(value, ['limit'], { maxChars: 1000 });
                            error.notes = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.notes} />
                    <Stack className='divider'></Stack>
                    <SelectSubsidiary selected={data.subsidiary}
                        label={'Subsidiary'}
                        disabled={submitting}
                        required
                        onChange={(value) => {
                            let _data = data;
                            _data.subsidiary = value;

                            const validation = ValidationService.combination(value, ['required'], {});
                            error.subsidiary = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <SelectShipType label={"Ship type"}
                        selected={data.type}
                        disabled={submitting}
                        required={true}
                        onChange={(value) => {
                            let _data = data;
                            _data.type = value;

                            const validation = ValidationService.combination(value, ['required'], {});
                            error.type = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <SelectCountry label={"Flag"}
                        selected={data.flag}
                        disabled={submitting}
                        required={true}
                        onChange={(value) => {
                            let _data = data;
                            _data.flag = value;

                            const validation = ValidationService.combination(value, ['required'], {});
                            error.flag = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }} />
                    <Stack className='divider'></Stack>
                    <Label size={'small'}>Vessel Details</Label>
                    <TextField label={"Year made"}
                        disabled={submitting}
                        value={data.yearMade}
                        required
                        onChange={(evt: any, value?: string) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.yearMade = (value || "");

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 4 });
                                error.yearMade = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.yearMade} />
                    <TextField label={"Class"}
                        disabled={submitting}
                        value={data.class}
                        required
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.class = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                            error.class = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.class} />
                    <TextField label={"Contruction / Konstruksi"}
                        value={data.construction}
                        disabled={submitting}
                        required
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.construction = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                            error.construction = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.construction} />
                    <TextField label={"Call sign / Panggilan"}
                        value={data.callSign}
                        disabled={submitting}
                        required
                        onChange={(evt: any, value?: string) => {
                            let _data = data;
                            _data.callSign = value;

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                            error.callSign = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }}
                        errorMessage={error.callSign} />
                    <Stack className='divider'></Stack>
                    <Label size={'small'}>Size Details</Label>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <TextField label={"LOA / Panjang keseluruhan"}
                            disabled={submitting}
                            suffix='m'
                            required={true}
                            value={(data.loa || "") + ""}
                            styles={{ root: { width: '50%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.loa = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.loa = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.loa} />
                        <TextField label={"LBP / Panjang antara garis gerak"}
                            suffix='m'
                            disabled={submitting}
                            required={true}
                            value={(data.lbp || "") + ""}
                            styles={{ root: { width: '50%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.lbp = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.lbp = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.lbp} />
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <TextField label={"Breadth / Lebar"}
                            suffix='m'
                            disabled={submitting}
                            required={true}
                            value={(data.breadth || "") + ""}
                            styles={{ root: { width: '50%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.breadth = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.breadth = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.breadth} />
                        <TextField label={"Dept / Dalam"}
                            suffix='m'
                            disabled={submitting}
                            required={true}
                            value={(data.depth || "") + ""}
                            styles={{ root: { width: '50%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.depth = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.depth = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.depth} />
                    </Stack>
                    <TextField label={"Maximum full load / Draft"}
                        suffix='m'
                        disabled={submitting}
                        required={true}
                        value={(data.maximumLoad || "") + ""}
                        styles={{ root: { width: 'calc(50% - 10px)' } }}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.maximumLoad = (value || "");

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                error.maximumLoad = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.maximumLoad} />
                    <Stack className='divider'></Stack>
                    <Label size={'small'}>Tonage Details</Label>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <TextField label={"Gross tonage"}
                            suffix='kg'
                            disabled={submitting}
                            required={true}
                            value={(data.grossTonage || "") + ""}
                            styles={{ root: { width: '50%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.grossTonage = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.grossTonage = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.grossTonage} />
                        <TextField label={"Nett tonage"}
                            suffix='kg'
                            disabled={submitting}
                            required={true}
                            value={(data.nettTonage || "") + ""}
                            styles={{ root: { width: '50%' } }}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.nettTonage = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.nettTonage = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.nettTonage} />
                    </Stack>
                </> : null
            }
        </Stack>
    </Panel>
};

export default VesselForm;
